import { getAccessToken, login, logout, refreshTokenSilently, refreshUser } from '@app/shared/auth'
import { appConfig } from '@app/shared/config'
import { customErrorDialog, dialog } from '@app/shared/dialog'
import { loading } from '@app/shared/loader'
import type { SpBaseResponse } from '@backend-models/common/request/sp-base-response'
import { SpErrorCode } from '@backend-models/common/request/sp-error-code'

// Create new fetch request options
type RequestOptions = RequestInit & {
  hideLoading?: boolean
  auth?: boolean
  json?: boolean
  /** When true, non-OK responses are returned without showing the global error dialog. */
  suppressErrorDialog?: boolean
}

// Validate that the request URL targets the allowed API origin
const isAllowedUrl = (url: URL | RequestInfo): boolean => {
  const resolved = new URL(url instanceof Request ? url.url : url.toString(), window.location.origin)
  const allowed = new URL(appConfig.api)
  return resolved.origin === allowed.origin
}

// Counter for number of request
let requestCount = 0

/**
 * Custom request method
 * @param url
 * @param options
 * @returns Promise
 */
export const request = async (url: URL | RequestInfo, options?: RequestOptions) => {
  let response = new Response()
  // Validate request target before any fetch calls
  if (!isAllowedUrl(url)) {
    throw new Error(`Request blocked: URL does not match allowed API origin`)
  }
  try {
    // Increment requests by one
    requestCount++
    // Show loading if not disabled
    await loading(!(options?.hideLoading ?? false))
    // If auth option is provided set up access token header and impersonation token
    if (options?.auth) {
      const impersonationToken = sessionStorage.getItem('impersonation-token')
      Object.assign(options, {
        headers: {
          ...options.headers,
          Authorization: `Bearer ${getAccessToken()}`,
          ...(impersonationToken && { 'X-Impersonation-Token': impersonationToken }),
        },
      })
    }
    // If json option is provided set up content type
    if (options?.json) {
      Object.assign(options, { headers: { ...options.headers, 'Content-Type': `application/json` } })
    }
    // Execute request
    response = await fetch(url, options)
    // If response is 401 and auth option is true try to refresh token and on success repeat request otherwise redirect to login
    if (response.status === 401 && options?.auth) {
      const refreshed = await refreshTokenSilently()
      if (!refreshed) {
        login()
        return Promise.reject(response)
      }
      await refreshUser()
      const impersonationTokenRetry = sessionStorage.getItem('impersonation-token')
      Object.assign(options, {
        headers: {
          ...options.headers,
          Authorization: `Bearer ${getAccessToken()}`,
          ...(impersonationTokenRetry && { 'X-Impersonation-Token': impersonationTokenRetry }),
        },
      })
      response = await fetch(url, options)
    }
    // If user account has been disabled in Okta, force a full logout
    if (response.status === 403) {
      const errorClone = (await response.clone().json()) as SpBaseResponse
      const isDisabled = errorClone.errors?.some((e) => e.errorCode === SpErrorCode.UserDisabled)
      if (isDisabled) {
        const message =
          errorClone.errors?.map((e) => e.clientMessage || e.error).join(', ') ||
          'Your account has been disabled. Please contact your administrator.'
        await dialog({ header: 'Account Disabled', message, modal: true })
        await logout()
        return Promise.reject(response)
      }
    }
    // If response status is not ok display message (unless caller handles errors)
    if (!response.ok && !options?.suppressErrorDialog) {
      if (response.status === 413) {
        await customErrorDialog({
          header: `Error ${response.status}`,
          message: 'The file is too large to upload. Hard limit is 50MB. Please choose a smaller file and try again.',
          code: response.status,
        })
      } else {
        let message = `Unknown error occured. Contact support.`
        try {
          const error = (await response.clone().json()) as SpBaseResponse
          message = error.errors?.map((e) => e.clientMessage || e.error).join(', ') || message
        } catch {
          // Non-JSON bodies (e.g. proxy errors) still need a usable message
          message = response.statusText || message
        }
        await customErrorDialog({ header: `Error ${response.status}`, message, code: response.status })
      }
    }
  } catch (error: unknown) {
    // Callers that opted out of error dialogs stay silent for network-level
    // failures too — they receive the rejection and degrade on their own.
    if (!options?.suppressErrorDialog) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        await dialog({
          header: `Error`,
          message: `An unexpected error occurred. Please check you internet connection or refresh the page and try again.`,
          modal: true,
        })
      } else {
        await dialog({ header: `Error`, message: `${url} Unknown error occured. Contact support.` })
      }
    }
    return Promise.reject(error)
  } finally {
    // Decrement request count by one and hide loading indicator if no more requests remain
    requestCount--
    if (requestCount === 0) {
      loading(false)
    }
  }
  return response
}
